import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Form, ToggleButton } from 'react-bootstrap';
import { IoMdAdd } from "react-icons/io";
import { IoMdClose } from "react-icons/io";

const UploadContent = ({ showModal, setShowModal }) => {
    const [step, setStep] = useState(1);
    const [files, setFiles] = useState([]);
    const [, setIsConfirmed] = useState(false);
    const [videoDurations, setVideoDurations] = useState([]);
    const [styleMood, setStyleMood] = useState('');
    const [musicUrls, setMusicUrls] = useState(['']);
    const [videoLength, setVideoLength] = useState('');
    const [videoLengthError, setVideoLengthError] = useState('');
    const [preferences, setPreferences] = useState([]);
    const [videoScript, setVideoScript] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    // Handle file selection
    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);

        setFiles((prevFiles) => {
            const existingNames = new Set(prevFiles.map(file => file.name));
            const filteredNewFiles = newFiles.filter(file => !existingNames.has(file.name));
            return [...prevFiles, ...filteredNewFiles];
        });

        newFiles.forEach((file, index) => {
            const videoElement = document.createElement('video');
            videoElement.src = URL.createObjectURL(file);

            videoElement.onloadedmetadata = () => {
                const duration = formatDuration(videoElement.duration);
                setVideoDurations((prevDurations) => {
                    const updatedDurations = [...prevDurations];
                    updatedDurations[index] = duration;
                    return updatedDurations;
                });
            };
        });
    };

    const preferenceOptions = [
        "Color Grading",
        "Transitions",
        "Animations",
        "Text Overlays",
        "Special Effects"
    ];

    const removeFile = (index) => {
        setFiles(files.filter((_, i) => i !== index));
        setVideoDurations(videoDurations.filter((_, i) => i !== index));
    };

    const formatDuration = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes}:${secs < 10 ? '0' : ''}${secs} minutes`;
    };

    const handleVideoLengthChange = (e) => {
        const input = e.target.value;
        setVideoLength(input);

        const timePattern = /^(\d+:\d{1,2}|\d+)$/;

        if (!timePattern.test(input)) {
            setVideoLengthError("Please enter a valid format (e.g., 2:30m)");
        } else {
            setVideoLengthError('');
        }
    };

    const handleNextStep = () => {
        if (step === 3) {
            setShowModal(false); // Hide main modal
            setShowSuccessModal(true); // Show success modal
        } else {
            setStep(step + 1);
        }
    };

    useEffect(() => {
        if (showSuccessModal) {
            const timer = setTimeout(() => {
                setShowSuccessModal(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showSuccessModal]);


    return (
        <>
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="md">
                <Modal.Header closeButton>
                    <Modal.Title>Upload Your Footage</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='upload-footage-body'>
                        {step === 1 && (
                            <div className='upload-videos'>
                                <h5>Step 1: Upload Your Videos</h5>

                                {/* Hidden input that opens on "Add More" click */}
                                <input
                                    id="video-upload"
                                    type="file"
                                    multiple
                                    accept="video/*"
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />
                                <div className='upload-videos-element'>
                                    <label htmlFor="video-upload" className="btn btn-primary upload-videos-button">
                                        Add Videos
                                    </label>

                                    <p>{files.length} video{files.length !== 1 && 's'} selected</p>
                                </div>

                                <Row className="thumbnail-container video-thumbnail-component">
                                    {files.map((file, index) => (
                                        <Col key={index} xs={12} md={12} lg={6} className="video-thumbnail position-relative">
                                            <div className="embed-responsive embed-responsive-16by9">
                                                <video
                                                    className="embed-responsive-item"
                                                    src={URL.createObjectURL(file)}
                                                    controls
                                                    style={{ width: '100%', height: 'auto' }} // Ensuring video fits within container
                                                />
                                            </div>
                                            <button
                                                className="video-thumbnail-btn"
                                                onClick={() => removeFile(index)}
                                            >
                                                <IoMdClose />
                                            </button>
                                        </Col>
                                    ))}
                                </Row>

                            </div>
                        )}

                        {step === 2 && (
                            <div className='upload-instruction-videos'>
                                <h5>Step 2: Add Instructions</h5>
                                <div className='show-videos'>
                                    {files.map((file, index) => (
                                        <div key={index} className="instruction-videos">
                                            <div className='video-items'>
                                                <div className="embed-responsive embed-responsive-16by9 v-item">
                                                    <video
                                                        className="embed-responsive-item"
                                                        src={URL.createObjectURL(file)}
                                                        controls
                                                        style={{ width: '100%', height: 'auto' }}
                                                    />
                                                </div>
                                                <div className="video-info">
                                                    <p><strong>{file.name}</strong></p>
                                                    <p>{videoDurations[index] || 'Loading duration...'}</p>
                                                </div>
                                            </div>
                                            <button
                                                className="video-thumbnail-btn"
                                                onClick={() => removeFile(index)}
                                            >
                                                <IoMdClose />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <div className='instruction-add-flow'>
                                    <p>{files.length} video{files.length !== 1 && 's'} selected</p>
                                    <input
                                        id="video-upload"
                                        type="file"
                                        multiple
                                        accept="video/*"
                                        style={{ display: 'none' }}
                                        onChange={handleFileChange}
                                    />
                                    <label htmlFor="video-upload" className="btn btn-primary upload-videos-button">
                                        Add Videos
                                    </label>

                                </div>
                                <div className='instruction-form'>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Desired video length (e.g., 2:30 or 1:05 or 45)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="length"
                                            placeholder="e.g., 2:30m"
                                            value={videoLength}
                                            onChange={handleVideoLengthChange}
                                            isInvalid={!!videoLengthError}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {videoLengthError}
                                        </Form.Control.Feedback>
                                    </Form.Group>


                                    <Form.Group className="mb-3">
                                        <Form.Label>Style and mood</Form.Label>
                                        <Form.Select
                                            value={styleMood}
                                            onChange={(e) => setStyleMood(e.target.value)}
                                        >
                                            <option value="">Select Mood</option>
                                            <option value="Dynamic">Dynamic</option>
                                            <option value="Static">Static</option>
                                            <option value="Energetic">Energetic</option>
                                            <option value="Emotional">Emotional</option>
                                        </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="mb-3 m-url">
                                        <Row>
                                            <Col xs={9}>
                                                <Form.Label>Music or Sound Preferences</Form.Label>
                                                <span className='mb-3'>Reference videos or music URL you'd like us to consider</span>
                                            </Col>
                                            <Col xs={3} className='add-btn-wraper'>
                                                <Button
                                                    variant="outline-primary"
                                                    className='add-btn'
                                                    onClick={() => setMusicUrls([...musicUrls, ''])}
                                                >
                                                    <IoMdAdd />
                                                </Button>
                                            </Col>
                                        </Row>
                                        {musicUrls.map((url, index) => (
                                            <div key={index} className="url-filds mb-3">
                                                <div className="url-filds-inner">
                                                    <input
                                                        type="url"
                                                        className="form-control"
                                                        placeholder="https://youtube.com/..."
                                                        value={url}
                                                        onChange={(e) => {
                                                            const updated = [...musicUrls];
                                                            updated[index] = e.target.value;
                                                            setMusicUrls(updated);
                                                        }}
                                                    />
                                                </div>
                                                <div className='add-r-btn-wraper'>
                                                    {index > 0 && (
                                                        <Button
                                                            variant="danger"
                                                            className='remove-btn'
                                                            onClick={() => {
                                                                setMusicUrls((prev) => prev.filter((_, i) => i !== index));
                                                            }}
                                                        >
                                                            <IoMdClose />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Choose preferences</Form.Label>
                                        <div className="preferences-btn-group">
                                            {preferenceOptions.map((option, idx) => (
                                                <ToggleButton
                                                    key={idx}
                                                    id={`pref-${idx}`}
                                                    type="checkbox"
                                                    variant={preferences.includes(option) ? "primary" : "outline-primary"}
                                                    checked={preferences.includes(option)}
                                                    value={option}
                                                    onChange={() => {
                                                        setPreferences(prev =>
                                                            prev.includes(option)
                                                                ? prev.filter(item => item !== option)
                                                                : [...prev, option]
                                                        );
                                                    }}
                                                >
                                                    {option}
                                                </ToggleButton>
                                            ))}
                                        </div>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Add video scripts</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            placeholder="Write your script or notes here..."
                                            value={videoScript}
                                            onChange={(e) => setVideoScript(e.target.value)}
                                        />
                                    </Form.Group>

                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div className='upload-instruction-videos instruction-details'>
                                <h5>Step 3: Instructions Details</h5>
                                <div className='show-videos'>
                                    {files.map((file, index) => (
                                        <div key={index} className="instruction-videos">
                                            <div className='video-items'>
                                                <div className="embed-responsive embed-responsive-16by9 v-item">
                                                    <video
                                                        className="embed-responsive-item"
                                                        src={URL.createObjectURL(file)}
                                                        controls
                                                        style={{ width: '100%', height: 'auto' }}
                                                    />
                                                </div>
                                                <div className="video-info">
                                                    <p><strong>{file.name}</strong></p>
                                                    <p>{videoDurations[index] || 'Loading duration...'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className='instruction-details-component'>
                                    <div className='item'>
                                        <span>Desired video length</span>
                                        <p>2 : 30 minutes</p>
                                    </div>
                                    <div className='item'>
                                        <span>Style and mood</span>
                                        <p>Dynamic</p>
                                    </div>
                                    <div className='item'>
                                        <span>Music or sound preferences</span>
                                        <p>https://www.youtube.com/watch?v=t406bHa</p>
                                        <p>https://www.youtube.com/watch?v=t406bHa</p>
                                    </div>
                                    <div className='item'>
                                        <span>Selected preferences</span>
                                        <div className='btn-section-group'>
                                            <Button variant="info" size="sm" disabled>
                                                Transitions
                                            </Button>
                                            <Button variant="info" size="sm" disabled>
                                                Special effects
                                            </Button>
                                        </div>
                                    </div>
                                    <div className='item'>
                                        <span>Add video scripts</span>
                                        <p>Text on Screen : "A Weekend in Jaipur – The Pink City"

                                            Voiceover: "Welcome to Jaipur — a vibrant city where history, culture, and color come alive."</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    {step !== 1 && (
                        <Button variant="info" onClick={() => setStep(step - 1)}>
                            Back
                        </Button>
                    )}
                    <Button variant="primary" onClick={handleNextStep}>
                        {step === 3 ? 'Place order' : 'Continue'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
            show={showSuccessModal}
            onHide={() => setShowSuccessModal(false)}
            centered
            size="sm"
            className='upload-successful-modal'
            >
                <Modal.Header closeButton>
                    <Modal.Title>🎉 Congratulations!</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h5>Upload Successful</h5>
                    <p><strong>Your video files have been uploaded successfully.</strong></p>
                    <p>Our team will begin editing shortly based on your selected package and instructions.</p>
                    <p>You’ll be notified once the first draft is ready.</p>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default UploadContent;
